import { productKey } from './prices';

/**
 * Comanda rapidă (2026-10-28): restaurantele, magazinele și fabricile comandă aceleași
 * produse în fiecare săptămână și nu vor o cerere de ofertă de fiecare dată.
 * - prețul convenit: păstrat din oferta câștigătoare sau scris de mână, bun până la o dată;
 * - lista de comandă: produsele obișnuite ale unui loc, cu cantitatea obișnuită;
 * - comanda din nou: pentru fiecare produs, furnizorul cu cel mai bun preț convenit valabil;
 *   o comandă de achiziție pe furnizor, prin aprobare ca orice comandă.
 * Când prețurile expiră, cererea de ofertă le reînnoiește (aici se întoarce concurența).
 */

/** Cu câte zile înainte de expirare un preț e „expiră curând”. */
export const AGREED_SOON_DAYS = 14;

/** Cât ține implicit un preț păstrat dintr-o ofertă: 90 de zile. */
export const AGREED_DEFAULT_DAYS = 90;

export type AgreedStatus = 'valid' | 'soon' | 'expired';

export interface AgreedPrice {
  id: string;
  supplier: { id: string; name: string };
  desc: string;
  unit: string;
  /** productKey(desc, unit) */
  key: string;
  /** lei fără TVA */
  unitPrice: number;
  minQty: number | null;
  /** 'AAAA-LL-ZZ', ultima zi bună */
  validUntil: string;
  category: string;
  /** cererea din care vine; null = scris de mână */
  sourceRfqId: string | null;
  createdAt: string;
}

export interface OrderListItem {
  id: string;
  desc: string;
  unit: string;
  key: string;
  /** cantitatea obișnuită; null = se scrie de fiecare dată */
  qty: number | null;
}

export interface OrderList {
  id: string;
  name: string;
  siteId: string | null;
  items: OrderListItem[];
}

/** Ziua `iso` + `days`, tot 'AAAA-LL-ZZ'. */
export function addDaysIso(iso: string, days: number): string {
  const d = new Date(`${iso}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

export function agreedStatus(validUntil: string, today: string): AgreedStatus {
  if (validUntil < today) return 'expired';
  return validUntil <= addDaysIso(today, AGREED_SOON_DAYS) ? 'soon' : 'valid';
}

/**
 * Cel mai bun preț convenit încă bun pentru produs: cel mai mic; la egalitate, cel
 * care ține mai mult. null = niciun furnizor n-are preț convenit valabil.
 */
export function bestAgreed<T extends Pick<AgreedPrice, 'key' | 'unitPrice' | 'validUntil'>>(
  prices: T[],
  key: string,
  today: string,
): T | null {
  let best: T | null = null;
  for (const p of prices) {
    if (p.key !== key || p.validUntil < today) continue;
    if (
      !best ||
      p.unitPrice < best.unitPrice ||
      (p.unitPrice === best.unitPrice && p.validUntil > best.validUntil)
    ) {
      best = p;
    }
  }
  return best;
}

export interface ReorderLine {
  desc: string;
  unit: string;
  qty: number;
}

export interface ReorderGroup<T> {
  supplierId: string;
  lines: (ReorderLine & { price: T; belowMin: boolean })[];
  /** lei fără TVA */
  total: number;
}

/**
 * Comanda din nou, împărțită pe furnizori: fiecare produs la cel mai bun preț convenit.
 * Produsele fără preț convenit valabil rămân deoparte (`missing`): pentru ele se face
 * o cerere de ofertă.
 */
export function planReorder<
  T extends Pick<AgreedPrice, 'key' | 'unitPrice' | 'validUntil' | 'minQty'> & {
    supplier: { id: string };
  },
>(lines: ReorderLine[], prices: T[], today: string): { groups: ReorderGroup<T>[]; missing: ReorderLine[] } {
  const groups = new Map<string, ReorderGroup<T>>();
  const missing: ReorderLine[] = [];
  for (const line of lines) {
    if (!(line.qty > 0)) continue;
    const price = bestAgreed(prices, productKey(line.desc, line.unit), today);
    if (!price) {
      missing.push(line);
      continue;
    }
    const g = groups.get(price.supplier.id) ?? { supplierId: price.supplier.id, lines: [], total: 0 };
    g.lines.push({ ...line, price, belowMin: price.minQty != null && line.qty < price.minQty });
    g.total = Math.round((g.total + line.qty * price.unitPrice) * 100) / 100;
    groups.set(price.supplier.id, g);
  }
  return { groups: [...groups.values()], missing };
}

/** Produsele cu preț convenit, câte unul pe produs (cel mai bun), pentru alegerea din listă. */
export function agreedCatalog<
  T extends Pick<AgreedPrice, 'key' | 'unitPrice' | 'validUntil' | 'desc' | 'unit'>,
>(prices: T[], today: string): T[] {
  const keys = [...new Set(prices.map((p) => p.key))];
  return keys
    .map((k) => bestAgreed(prices, k, today))
    .filter((p): p is T => !!p)
    .sort((a, b) => a.desc.localeCompare(b.desc, 'ro'));
}

/** Ziua de azi în România, 'AAAA-LL-ZZ': prețul convenit ține până la sfârșitul zilei lui. */
export function todayRo(now: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Bucharest' }).format(now);
}
